import { Comment } from "@/components/comments/Comment.tsx";
import { FormResultForEmployeeProps } from "@/components/form-result/result-modal/components/types.ts";
import { CommentElement } from "@/components/questionnaire/components/CommentElement.tsx";
import { QuestionElementProps } from "@/components/questionnaire/components/element.type.ts";
import { getFormKey } from "@/components/questionnaire/questionnaire.types.ts";
import { ApiFormElementSelectOption } from "@/types/forms/form-view.types.ts";
import { Box, Checkbox, Radio, Stack, Text, Title, HtmlViewer } from "@skailer-com/uikit";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

const isSelected = (values: number[] | undefined, id: number) => values?.includes(id) || false;

const SelectOptionsGroup = ({
  options,
  isMultiple,
  selectedIds,
  onSelect,
  readOnly,
}: {
  options: ApiFormElementSelectOption[];
  isMultiple: boolean;
  selectedIds: number[];
  onSelect?: (id: number) => void;
  readOnly?: boolean;
}) => (
  <Stack gap="sm">
    {options.map((item) => {
      const sharedProps = {
        emoji: item.emoji || undefined,
        title: item.title || undefined,
        caption: item.description || undefined,
        variant: "accent" as const,
        checked: isSelected(selectedIds, item.id),
        onChange: onSelect ? () => onSelect(item.id) : undefined,
        readOnly,
      };
      return isMultiple ? (
        <Checkbox key={item.id} {...sharedProps} />
      ) : (
        <Radio key={item.id} {...sharedProps} />
      );
    })}
  </Stack>
);

export const SelectElement = ({ element }: QuestionElementProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const selectSettings = element.select_settings;
  if (!selectSettings) return null;

  const formElementName = getFormKey(element);
  const isMultiple = selectSettings.is_multiple || false;
  const hasError = !!errors[formElementName];

  return (
    <Stack gap={"2xl"} data-error={hasError || undefined}>
      {(selectSettings.title || selectSettings.description) && (
        <Stack gap="md">
          <Title
            header={selectSettings.title || undefined}
            size="md"
            required={selectSettings.is_required}
          />
          {selectSettings.description && <HtmlViewer content={selectSettings.description} />}
        </Stack>
      )}

      <Controller
        name={formElementName}
        control={control}
        render={({ field }) => {
          const values = Array.isArray(field.value) ? (field.value as number[]) : [];

          const handleSelectClick = (selectOptionId: number): void => {
            if (isMultiple) {
              field.onChange(
                values.includes(selectOptionId)
                  ? values.filter((id) => id !== selectOptionId)
                  : [...values, selectOptionId]
              );
            } else {
              field.onChange([selectOptionId]);
            }
          };

          return (
            <Stack>
              <SelectOptionsGroup
                options={selectSettings.options}
                isMultiple={isMultiple}
                selectedIds={values}
                onSelect={handleSelectClick}
              />
              {errors[formElementName]?.message && (
                <Box className="pt-2 pb-4">
                  <Text className="text-danger">{String(errors[formElementName]?.message)}</Text>
                </Box>
              )}
            </Stack>
          );
        }}
      />
      {selectSettings.is_comment_enabled && <CommentElement element={element} />}
    </Stack>
  );
};

export const SelectElementResult = ({ element, answer }: FormResultForEmployeeProps) => {
  const selectSettings = element.select_settings;
  const { t } = useTranslation();

  if (!selectSettings) return null;

  const selectedIds = answer?.answer_select_option_ids || [];
  const isMultiple = selectSettings.is_multiple || false;

  return (
    <>
      <Title
        header={selectSettings.title || t("surveys:question_type_select")}
        size="sm"
        paddingTop
        className={selectSettings.title ? "" : "text-secondary"}
      />
      <Stack gap={"md"} className={"mt-3"}>
        <SelectOptionsGroup
          options={selectSettings.options}
          isMultiple={isMultiple}
          selectedIds={selectedIds}
          readOnly
        />
        {answer && answer.comment && (
          <Comment text={answer.comment} employee={null} isShort={false} />
        )}
      </Stack>
    </>
  );
};
